import { ArrowUpCircle, ArrowDownCircle, X } from 'lucide-react';
import { colors } from '../../styles/colors';

function TransactionsList({
  transactions,
  selectedAccountId,
  onClose
}) {
  if (!selectedAccountId) {
    return null;
  }

  // Les transactions viennent du backend avec account_id déjà filtré (1 seul ID)
  const accountTransactions = transactions.filter(transaction => {
    return transaction.account_id === selectedAccountId;
  });

  const formatEuro = (amount) => {
    if (amount === null || amount === undefined || isNaN(amount)) return '0,00 €';
    return new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency: 'EUR'
    }).format(amount);
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('fr-FR');
  };

  // Sort by date descending
  const sortedTransactions = [...accountTransactions].sort((a, b) => {
    const dateA = new Date(a.transaction_date || 0);
    const dateB = new Date(b.transaction_date || 0);
    return dateB - dateA;
  });

  return (
    <div style={{
      marginTop: '32px',
      border: `1px solid ${colors.border}`,
      borderRadius: '12px',
      backgroundColor: 'white',
      overflow: 'hidden'
    }}>
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        padding: '16px 20px',
        borderBottom: `1px solid ${colors.border}`,
        backgroundColor: '#fafafa'
      }}>
        <h3 style={{
          fontSize: '16px',
          fontWeight: '600',
          color: colors.text.primary,
          margin: 0
        }}>
          Transactions ({accountTransactions.length})
        </h3>
        <button
          onClick={onClose}
          style={{
            background: 'none',
            border: 'none',
            cursor: 'pointer',
            padding: '4px',
            display: 'flex',
            alignItems: 'center',
            color: colors.text.secondary
          }}
        >
          <X size={20} />
        </button>
      </div>

      {sortedTransactions.length === 0 ? (
        <div style={{
          textAlign: 'center',
          padding: '40px 20px',
          color: colors.text.secondary
        }}>
          Aucune transaction pour ce compte
        </div>
      ) : (
        <div style={{ overflowX: 'auto' }}>
          <table style={{
            width: '100%',
            borderCollapse: 'collapse',
            fontSize: '14px'
          }}>
            <thead>
              <tr style={{
                backgroundColor: colors.background.light,
                borderBottom: `2px solid ${colors.border.medium}`
              }}>
                <th style={{
                  padding: '12px 16px',
                  textAlign: 'left',
                  fontWeight: '600',
                  color: colors.text.primary,
                  fontSize: '13px'
                }}>
                  Type
                </th>
                <th style={{
                  padding: '12px 16px',
                  textAlign: 'left',
                  fontWeight: '600',
                  color: colors.text.primary,
                  fontSize: '13px'
                }}>
                  Date
                </th>
                <th style={{
                  padding: '12px 16px',
                  textAlign: 'left',
                  fontWeight: '600',
                  color: colors.text.primary,
                  fontSize: '13px'
                }}>
                  Description
                </th>
                <th style={{
                  padding: '12px 16px',
                  textAlign: 'right',
                  fontWeight: '600',
                  color: colors.text.primary,
                  fontSize: '13px'
                }}>
                  Montant
                </th>
                <th style={{
                  padding: '12px 16px',
                  textAlign: 'left',
                  fontWeight: '600',
                  color: colors.text.primary,
                  fontSize: '13px'
                }}>
                  Contrepartie
                </th>
                <th style={{
                  padding: '12px 16px',
                  textAlign: 'left',
                  fontWeight: '600',
                  color: colors.text.primary,
                  fontSize: '13px'
                }}>
                  Catégorie
                </th>
                <th style={{
                  padding: '12px 16px',
                  textAlign: 'left',
                  fontWeight: '600',
                  color: colors.text.primary,
                  fontSize: '13px'
                }}>
                  Référence
                </th>
                <th style={{
                  padding: '12px 16px',
                  textAlign: 'left',
                  fontWeight: '600',
                  color: colors.text.primary,
                  fontSize: '13px'
                }}>
                  Statut
                </th>
              </tr>
            </thead>
            <tbody>
              {sortedTransactions.map((transaction, index) => {
                const amount = transaction.amount || 0;
                const isDebit = amount < 0;
                const description = transaction.description || 'Transaction';
                const date = transaction.transaction_date;
                const reference = transaction.external_id;
                const counterparty = transaction.counterparty;
                const category = transaction.category;
                const status = transaction.status;

                return (
                  <tr
                    key={transaction.id}
                    style={{
                      borderBottom: index < sortedTransactions.length - 1 ? `1px solid ${colors.border.light}` : 'none',
                      transition: 'background-color 0.2s'
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.backgroundColor = colors.background.light}
                    onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'white'}
                  >
                    <td style={{ padding: '12px 16px' }}>
                      {isDebit ? (
                        <ArrowDownCircle size={18} color={colors.status.error} />
                      ) : (
                        <ArrowUpCircle size={18} color={colors.status.success} />
                      )}
                    </td>
                    <td style={{
                      padding: '12px 16px',
                      color: colors.text.secondary,
                      whiteSpace: 'nowrap'
                    }}>
                      {formatDate(date)}
                    </td>
                    <td style={{
                      padding: '12px 16px',
                      color: colors.text.primary,
                      fontWeight: '500'
                    }}>
                      {description}
                    </td>
                    <td style={{
                      padding: '12px 16px',
                      textAlign: 'right',
                      fontWeight: '600',
                      color: isDebit ? colors.status.error : colors.status.success,
                      whiteSpace: 'nowrap'
                    }}>
                      {formatEuro(amount)}
                    </td>
                    <td style={{
                      padding: '12px 16px',
                      color: colors.text.secondary
                    }}>
                      {counterparty || '-'}
                    </td>
                    <td style={{ padding: '12px 16px' }}>
                      {category ? (
                        <span style={{
                          padding: '4px 8px',
                          backgroundColor: colors.primary.teal + '15',
                          color: colors.primary.teal,
                          borderRadius: '4px',
                          fontSize: '12px',
                          fontWeight: '500',
                          whiteSpace: 'nowrap'
                        }}>
                          {category}
                        </span>
                      ) : (
                        <span style={{ color: colors.text.secondary }}>-</span>
                      )}
                    </td>
                    <td style={{
                      padding: '12px 16px',
                      fontFamily: 'monospace',
                      fontSize: '12px',
                      color: colors.text.secondary
                    }}>
                      {reference || '-'}
                    </td>
                    <td style={{ padding: '12px 16px' }}>
                      {status && status !== 'completed' ? (
                        <span style={{
                          padding: '4px 8px',
                          backgroundColor: status === 'pending' ? colors.status.warning + '15' : colors.status.error + '15',
                          color: status === 'pending' ? colors.status.warning : colors.status.error,
                          borderRadius: '4px',
                          fontSize: '12px',
                          fontWeight: '500',
                          whiteSpace: 'nowrap'
                        }}>
                          {status}
                        </span>
                      ) : (
                        <span style={{
                          padding: '4px 8px',
                          backgroundColor: colors.status.success + '15',
                          color: colors.status.success,
                          borderRadius: '4px',
                          fontSize: '12px',
                          fontWeight: '500',
                          whiteSpace: 'nowrap'
                        }}>
                          completed
                        </span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export default TransactionsList;
